// React 18 compatible UJS implementation
import React from 'react';
import { createRoot } from 'react-dom/client';

// Import Toast notification system and make it globally available
import Toast from '../components/Toast';
window.Toast = Toast;

// Import RecentSearches and make it globally available
import '../components/RecentSearches';

// Support component names relative to this directory:
var componentRequireContext = require.context("components", true);

// Store roots for cleanup
const roots = new Map();

// Custom React 18 UJS implementation
const ReactRailsUJS = {
  CLASS_NAME_ATTR: 'data-react-class',
  PROPS_ATTR: 'data-react-props',

  // Get component constructor from require context
  getConstructor: function(className) {
    // Try to find the component in the require context
    const parts = className.split('.');
    const filename = parts[parts.length - 1];

    try {
      // Try direct filename match
      const component = componentRequireContext('./' + filename + '.js');
      return component.default || component;
    } catch (e) {
      // Try with the full path
      try {
        const component = componentRequireContext('./' + className.replace(/\./g, '/') + '.js');
        return component.default || component;
      } catch (e2) {
        console.error(`Cannot find component: '${className}'`);
        return null;
      }
    }
  },

  findDOMNodes: function(searchSelector) {
    const classNameAttr = ReactRailsUJS.CLASS_NAME_ATTR;
    let selector, parent;

    switch (typeof searchSelector) {
      case 'undefined':
        selector = '[' + classNameAttr + ']';
        parent = document;
        break;
      case 'object':
        selector = '[' + classNameAttr + ']';
        parent = searchSelector;
        break;
      case 'string':
        selector = searchSelector + '[' + classNameAttr + '], ' +
                   searchSelector + ' [' + classNameAttr + ']';
        parent = document;
        break;
      default:
        break;
    }

    return parent.querySelectorAll(selector);
  },

  mountComponents: function(searchSelector) {
    const nodes = ReactRailsUJS.findDOMNodes(searchSelector);

    for (let i = 0; i < nodes.length; ++i) {
      const node = nodes[i];
      const className = node.getAttribute(ReactRailsUJS.CLASS_NAME_ATTR);
      const Constructor = ReactRailsUJS.getConstructor(className);
      const propsJson = node.getAttribute(ReactRailsUJS.PROPS_ATTR);
      const props = propsJson && JSON.parse(propsJson);

      if (!Constructor) {
        const message = "Cannot find component: '" + className + "'";
        console.error("[react-rails] " + message + " for element", node);
        continue;
      }

      // Use React 18's createRoot API
      let root = roots.get(node);
      if (!root) {
        root = createRoot(node);
        roots.set(node, root);
      }
      root.render(React.createElement(Constructor, props));
    }
  },

  unmountComponents: function(searchSelector) {
    const nodes = ReactRailsUJS.findDOMNodes(searchSelector);

    for (let i = 0; i < nodes.length; ++i) {
      const node = nodes[i];
      const root = roots.get(node);
      if (root) {
        root.unmount();
        roots.delete(node);
      }
    }
  },

  handleMount: function(e) {
    let target = undefined;
    if (e && e.target) {
      target = e.target;
    }
    ReactRailsUJS.mountComponents(target);
  },

  handleUnmount: function(e) {
    let target = undefined;
    if (e && e.target) {
      target = e.target;
    }
    ReactRailsUJS.unmountComponents(target);
  },

  detectEvents: function() {
    // Turbolinks support
    if (typeof Turbolinks !== 'undefined' && Turbolinks.supported) {
      document.addEventListener('turbolinks:load', ReactRailsUJS.handleMount);
      document.addEventListener('turbolinks:before-render', ReactRailsUJS.handleUnmount);
    } else {
      // Fallback to DOMContentLoaded
      document.addEventListener('DOMContentLoaded', ReactRailsUJS.handleMount);
    }
  }
};

// Initialize
if (typeof window !== "undefined") {
  ReactRailsUJS.detectEvents();
}

// Export for potential use elsewhere
window.ReactRailsUJS = ReactRailsUJS;
